import type { CSSProperties, FC } from 'react'
import type { WallTile as WallTileData } from '@/constants/home'

import { Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/kit/button'
import { Photo } from '@/components/kit/photo'
import { PlatformIcon } from '@/components/kit/platform-icon'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { contentWall } from '@/constants/home'

const WallTile: FC<{ tile: WallTileData }> = ({ tile }) => {
    return (
        <div className='relative aspect-[9/16] overflow-hidden rounded-2xl shadow-[0_20px_40px_-26px_rgb(30_30_30/0.6)] transition-[transform,box-shadow] duration-500 ease-expo hover:z-10 hover:scale-[1.07] hover:shadow-[0_40px_70px_-30px_rgb(30_30_30/0.7)]'>
            {tile.kind === 'photo' ? (
                <>
                    <Photo
                        src={tile.src}
                        alt=''
                        sizes='(min-width: 1024px) 220px, 45vw'
                        position={tile.position}
                        className='absolute inset-0'
                    />
                    <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/25' />
                    <span className='absolute left-1/2 top-1/2 flex size-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/25 text-white backdrop-blur-md'>
                        <Play className='size-4 translate-x-px fill-current' />
                    </span>
                    <div className='absolute inset-x-0 bottom-0 p-3 text-white'>
                        <p className='text-xs font-semibold leading-tight'>{tile.caption}</p>
                    </div>
                </>
            ) : (
                <div
                    className='flex h-full flex-col justify-end p-4'
                    style={{ backgroundColor: tile.bg, color: tile.fg }}
                >
                    <p className='font-heading text-[clamp(1.1rem,1.6vw,1.45rem)] font-bold leading-[0.98] tracking-[-0.04em]'>
                        {tile.text}
                    </p>
                </div>
            )}

            {tile.kind === 'type' && (
                <span className='absolute right-3 top-3 flex size-7 items-center justify-center rounded-full bg-black/15 backdrop-blur-md' style={{ color: tile.fg }}>
                    <Play className='size-3 translate-x-px fill-current' />
                </span>
            )}

            <span
                className='absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-black/25 px-2 py-1 backdrop-blur-md'
                style={{ color: tile.kind === 'type' ? tile.fg : '#ffffff', backgroundColor: tile.kind === 'type' ? 'rgb(255 255 255 / 0.18)' : undefined }}
            >
                <PlatformIcon
                    name={tile.platform}
                    className='size-3'
                />
                <span className='font-mono text-[10px]'>{tile.views} views</span>
            </span>
        </div>
    )
}

export const ContentWall: FC = () => {
    return (
        <section
            id='content'
            aria-labelledby='content-title'
            className='relative overflow-hidden py-20 md:py-28'
        >
            <div className='mx-auto grid max-w-[1400px] items-center gap-12 px-5 md:px-10 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-4'>
                    <SectionLabel index='04'>The wall</SectionLabel>

                    <SplitText
                        as='h2'
                        id='content-title'
                        lines={['Things people', 'sent to a friend.']}
                        italic='sent'
                        className='mt-5 text-[clamp(2.75rem,5.6vw,5.25rem)] font-bold leading-[0.9] tracking-[-0.045em]'
                    />

                    <Reveal
                        as='p'
                        delay={200}
                        className='mt-7 max-w-sm text-lg leading-relaxed text-stone'
                    >
                        A few tiles from this year, all made in our studio. Hover a column to pause it. Every one of these had a job, and most of them did it.
                    </Reveal>

                    <Reveal
                        delay={300}
                        className='mt-8 grid max-w-sm grid-cols-2 gap-3'
                    >
                        <div className='rounded-2xl border border-black/5 bg-paper p-4'>
                            <p className='font-heading text-3xl font-bold tracking-[-0.035em]'>3,900</p>
                            <p className='mt-1 font-mono text-[11px] text-stone'>edits this year</p>
                        </div>
                        <div className='rounded-2xl border border-black/5 bg-paper p-4'>
                            <p className='font-heading text-3xl font-bold tracking-[-0.035em]'>61M</p>
                            <p className='mt-1 font-mono text-[11px] text-stone'>views, give or take</p>
                        </div>
                    </Reveal>

                    <Reveal
                        delay={400}
                        className='mt-8'
                    >
                        <Button
                            href='/work'
                            cursor='Open'
                        >
                            Open the case notes
                        </Button>
                    </Reveal>
                </div>

                <Reveal
                    delay={150}
                    className='lg:col-span-8'
                >
                    <div
                        aria-hidden='true'
                        className='relative h-[560px] overflow-hidden rounded-[2rem] bg-sand md:h-[720px] md:rounded-[2.5rem] lg:h-[800px]'
                    >
                        <div className='absolute -inset-x-[14%] -inset-y-[22%] grid -rotate-[8deg] grid-cols-2 gap-3 md:grid-cols-3 md:gap-4'>
                            {contentWall.map((column, columnIndex) => (
                                <div
                                    key={columnIndex}
                                    className={cn('relative', columnIndex === 2 && 'hidden md:block')}
                                >
                                    <div
                                        className={cn(
                                            'pause-on-hover flex flex-col',
                                            columnIndex === 1 ? 'animate-wall-down' : 'animate-wall-up'
                                        )}
                                        style={{ animationDuration: `${[64, 78, 70][columnIndex]}s` } as CSSProperties}
                                    >
                                        {[...column, ...column].map((tile, tileIndex) => (
                                            <div
                                                key={tileIndex}
                                                className='pb-3 md:pb-4'
                                                data-cursor='Play'
                                            >
                                                <WallTile tile={tile} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className='pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-sand to-transparent' />
                        <div className='pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-sand to-transparent' />
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
